import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { Field } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FormPage, TextBox, TextArea, DatePicker, NumericBox } from '../../../ui';
import { ErrorDismissButton } from '../../shared/components';
import {
  loadMyClaimFormPage,
  saveMyClaim,
  uploadAttachment,
  reportInvalidAttachment,
  clearUploadError,
  cleanupMyClaimFormPage,
  openDeleteAttachmentDialog,
  closeDeleteAttachmentDialog,
  confirmDeleteAttachment
} from '../actions/my-claim-form-page-actions';
import { withRouter } from '../../../components/withRouter';

class MyClaimFormPage extends React.PureComponent {

  constructor() {
    super();
    this.handleValidate = this.handleValidate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleClearUploadError = this.handleClearUploadError.bind(this);
    this.handleDeleteAttachmentClick = this.handleDeleteAttachmentClick.bind(this);
    this.handleDeleteAttachmentDialogClose = this.handleDeleteAttachmentDialogClose.bind(this);
    this.handleDeleteAttachmentConfirm = this.handleDeleteAttachmentConfirm.bind(this);
  }

  async componentDidMount() {
    const { claimId } = this.props.params;
    await this.props.loadMyClaimFormPage(claimId);
  }

  componentWillUnmount() {
    this.props.cleanupMyClaimFormPage();
  }

  render() {
    const localizationService = provideLocalizationService(this);
    const { claimId } = this.props.params;
    const isEdit = !!claimId;
    return (
      <>
        <FormPage
          title={localizationService.toLanguageString(isEdit ? 'myGeneralCompetencyClaims.editTitle' : 'myGeneralCompetencyClaims.formTitle')}
          breadcrumbItems={[
            {
              id: 'my_general_competency_hours',
              text: localizationService.toLanguageString('myGeneralCompetencyHours.listTitle')
            },
            {
              id: 'my_general_competency_claim',
              text: localizationService.toLanguageString(isEdit ? 'myGeneralCompetencyClaims.editTitle' : 'myGeneralCompetencyClaims.formTitle'),
              disabled: true
            }
          ]}
          onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
          onBackClick={this.handleBackClick}
          renderBackButton={true}
          validationResult={this.props.validationResult}
          loading={this.props.loading}
          initialValues={this.props.claim}
          localizationService={localizationService}
          onSubmit={this.handleSubmit}
          onCancel={this.handleCancel}
          validator={this.handleValidate}
          render={() => (
            <>
              <Field
                name='title'
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.title')}
                component={TextBox}
                width='320px'
              />
              <Field
                name='organizer'
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.organizer')}
                component={TextBox}
                width='320px'
              />
              <Field
                name='occurredAt'
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.occurredAt')}
                component={DatePicker}
                width='320px'
              />
              <Field
                name='minutes'
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.minutes')}
                component={NumericBox}
                width='320px'
                min={1}
                format='n0'
              />
              <Field
                name='description'
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.description')}
                component={TextArea}
                autoSize={true}
                width='320px'
              />
              <div className='k-form-field' style={{ width: '320px' }}>
                <label className='k-label'>
                  {localizationService.toLanguageString('myGeneralCompetencyClaims.attachment')}
                </label>
                {this.props.attachmentFilename
                  ? <span>
                    {this.props.attachmentFilename}
                    {' '}
                    <button
                      type='button'
                      className='k-button k-button-sm k-button-flat k-button-flat-base'
                      onClick={this.handleDeleteAttachmentClick}
                    >
                      <span className='k-icon k-i-delete'></span>
                    </button>
                  </span>
                  : <input
                    type='file'
                    className='k-input'
                    accept='.pdf,application/pdf'
                    onChange={this.handleFileChange}
                    disabled={this.props.uploading}
                  />
                }
                {this.props.uploading && <span>{localizationService.toLanguageString('myGeneralCompetencyClaims.uploading')}</span>}
                {this.props.uploadError &&
                  <span className='k-form-error'>
                    {this.props.uploadError}
                    <ErrorDismissButton onClick={this.handleClearUploadError} />
                  </span>
                }
              </div>
            </>
          )}
        />
        {this.props.isDeleteAttachmentDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleDeleteAttachmentDialogClose}>
            <p>{localizationService.toLanguageString('myGeneralCompetencyClaims.deleteAttachmentConfirm')}</p>
            <DialogActionsBar>
              <Button themeColor='primary' onClick={this.handleDeleteAttachmentConfirm}>
                {localizationService.toLanguageString('buttons.delete')}
              </Button>
              <Button onClick={this.handleDeleteAttachmentDialogClose}>
                {localizationService.toLanguageString('buttons.cancel')}
              </Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  handleValidate(values, localizationService, validationResult, modified) {
    const errors = {};
    const maxTitleLength = 500;
    const maxOrganizerLength = 500;
    const maxDescriptionLength = 5000;
    if (!values.title) {
      errors.title = localizationService.toLanguageString('validation.required');
    } else if (values.title.length > maxTitleLength) {
      errors.title = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxTitleLength})`;
    }
    if (!values.organizer) {
      errors.organizer = localizationService.toLanguageString('validation.required');
    } else if (values.organizer.length > maxOrganizerLength) {
      errors.organizer = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxOrganizerLength})`;
    }
    if (!values.occurredAt) {
      errors.occurredAt = localizationService.toLanguageString('validation.required');
    }
    if (!values.minutes || values.minutes <= 0) {
      errors.minutes = localizationService.toLanguageString('validation.required');
    }
    if (values.description && values.description.length > maxDescriptionLength) {
      errors.description = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxDescriptionLength})`;
    }
    if (!modified.title && validationResult?.errors?.title) {
      errors.title = validationResult.errors.title;
    }
    if (!modified.organizer && validationResult?.errors?.organizer) {
      errors.organizer = validationResult.errors.organizer;
    }
    return errors;
  }

  handleClearUploadError() {
    this.props.clearUploadError();
  }

  handleFileChange(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) {
      return;
    }
    const isPdf = file.name.toLowerCase().endsWith('.pdf')
      && (file.type === 'application/pdf' || file.type === '');
    if (!isPdf) {
      const localizationService = provideLocalizationService(this);
      this.props.reportInvalidAttachment(localizationService.toLanguageString('myGeneralCompetencyClaims.invalidFileType'));
      return;
    }
    this.props.uploadAttachment(file);
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'my_general_competency_hours') {
      this.props.navigate('/my_general_competency_hours');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleCancel() {
    this.props.navigate(-1);
  }

  handleDeleteAttachmentClick() {
    this.props.openDeleteAttachmentDialog();
  }

  handleDeleteAttachmentDialogClose() {
    this.props.closeDeleteAttachmentDialog();
  }

  handleDeleteAttachmentConfirm() {
    this.props.confirmDeleteAttachment();
  }

  handleSubmit(payload) {
    if (this.props.uploadError) {
      return;
    }
    const { claimId } = this.props.params;
    this.props.saveMyClaim({
      ...payload,
      id: claimId ? parseInt(claimId) : null,
      attachmentId: this.props.attachmentId
    });
  }
}

registerForLocalization(MyClaimFormPage);

const mapStateToProps = state => ({
  loading: state.myGeneralCompetencyClaimFormPage.loading,
  claim: state.myGeneralCompetencyClaimFormPage.claim,
  validationResult: state.myGeneralCompetencyClaimFormPage.validationResult,
  uploading: state.myGeneralCompetencyClaimFormPage.uploading,
  attachmentId: state.myGeneralCompetencyClaimFormPage.attachmentId,
  attachmentFilename: state.myGeneralCompetencyClaimFormPage.attachmentFilename,
  uploadError: state.myGeneralCompetencyClaimFormPage.uploadError,
  isDeleteAttachmentDialogVisible: state.myGeneralCompetencyClaimFormPage.isDeleteAttachmentDialogVisible
});

const mapDispatchToProps = dispatch => ({
  loadMyClaimFormPage: (claimId) => dispatch(loadMyClaimFormPage(claimId)),
  saveMyClaim: (payload) => dispatch(saveMyClaim(payload)),
  uploadAttachment: (file) => dispatch(uploadAttachment(file)),
  reportInvalidAttachment: (message) => dispatch(reportInvalidAttachment(message)),
  clearUploadError: () => dispatch(clearUploadError()),
  cleanupMyClaimFormPage: () => dispatch(cleanupMyClaimFormPage()),
  openDeleteAttachmentDialog: () => dispatch(openDeleteAttachmentDialog()),
  closeDeleteAttachmentDialog: () => dispatch(closeDeleteAttachmentDialog()),
  confirmDeleteAttachment: () => dispatch(confirmDeleteAttachment())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyClaimFormPage));
